import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
} from "@mui/material";

import {
  loadModelViewer,
  resolveModelViewerAssetUrl,
} from "./modelViewerLoader";
import {
  getViewerPanelPaddingSx,
  viewerActionButtonIconSx,
  viewerActionButtonLabelSx,
  viewerPanelBodySx,
  viewerPanelSectionHeadingSx,
  viewerPillActionButtonSx,
} from "../unityWebViewer/panelStyles";

const EMPTY_CREDITS_MESSAGE =
  "Credits and licensing information has not been added yet.";

const asArray = (value) => (Array.isArray(value) ? value : []);
const textValue = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";
const hasCreditsContent = (credits) =>
  [
    credits?.bodyBlocks,
    credits?.licensedAssets,
    credits?.attributions,
    credits?.funding,
  ].some((section) => asArray(section).length > 0);

const ExternalLink = ({ children, href, ...props }) => {
  if (!href) {
    return children;
  }

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: "#8be7d7" }}
      {...props}
    >
      {children}
    </Link>
  );
};

const CreditSection = ({ children, compact, title }) => (
  <Box component="section" sx={{ mt: compact ? 2.25 : 4 }}>
    <Typography
      component="h2"
      sx={{
        mb: compact ? 1 : 1.5,
        ...(compact
          ? viewerPanelSectionHeadingSx
          : {
              color: "inherit",
              fontSize: "1.2rem",
              fontWeight: 800,
              lineHeight: 1.25,
            }),
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const BodyBlock = ({ block, compact }) => {
  const type = textValue(block?.type).toLowerCase();
  const title = textValue(block?.title);
  const content = textValue(block?.markdown || block?.text);

  if (!title && !content) {
    return null;
  }

  if (type === "heading") {
    return (
      <Typography
        component="h2"
        sx={{
          mt: compact ? 2 : 3,
          fontSize: compact ? "1rem" : "1.3rem",
          fontWeight: 800,
        }}
      >
        {title || content}
      </Typography>
    );
  }

  return (
    <Box
      sx={
        type === "callout"
          ? {
              borderLeft: "3px solid rgba(70, 205, 181, 0.8)",
              borderRadius: "0 8px 8px 0",
              backgroundColor: "rgba(70, 205, 181, 0.08)",
              px: compact ? 1.25 : 2,
              py: compact ? 1 : 1.5,
            }
          : undefined
      }
    >
      {title && (
        <Typography
          component="h3"
          sx={{
            mb: compact ? 0.35 : 0.5,
            fontSize: compact ? "0.88rem" : undefined,
            fontWeight: 750,
          }}
        >
          {title}
        </Typography>
      )}
      {content && (
        <Typography
          sx={{
            ...(compact ? viewerPanelBodySx : undefined),
            whiteSpace: "pre-wrap",
            lineHeight: compact ? viewerPanelBodySx.lineHeight : 1.65,
          }}
        >
          {content}
        </Typography>
      )}
    </Box>
  );
};

const ModelViewerPreview = ({ asset, compact }) => {
  const modelViewerRef = useRef(null);
  const [loadStatus, setLoadStatus] = useState("loading");
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const modelViewer = asset?.modelViewer;
  const resolvedSrc = resolveModelViewerAssetUrl(modelViewer?.src);
  const resolvedPoster = resolveModelViewerAssetUrl(modelViewer?.poster);

  useEffect(() => {
    let cancelled = false;

    Promise.resolve(loadModelViewer()).catch((error) => {
      if (!cancelled) {
        setLoadStatus("error");
        console.error("Model preview failed to load:", error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const element = modelViewerRef.current;

    if (!element) {
      return undefined;
    }

    setLoadStatus("loading");

    const handleLoad = () => setLoadStatus("loaded");
    const handleError = (event) => {
      setLoadStatus("error");
      console.error("Model asset failed to load:", resolvedSrc, event);
    };

    element.addEventListener("load", handleLoad);
    element.addEventListener("error", handleError);

    // model-viewer may finish a cached load before this effect attaches its
    // native event listeners. Its public `loaded` flag is the authoritative
    // fallback for that race.
    if (element.loaded) {
      setLoadStatus("loaded");
    }

    return () => {
      element.removeEventListener("load", handleLoad);
      element.removeEventListener("error", handleError);
    };
  }, [resolvedSrc]);

  useEffect(() => {
    const element = modelViewerRef.current;

    if (prefersReducedMotion && typeof element?.pause === "function") {
      element.pause();
    }
  }, [loadStatus, prefersReducedMotion]);

  const preview = React.createElement("model-viewer", {
    alt: textValue(modelViewer?.alt) || textValue(asset?.title),
    "animation-name": textValue(modelViewer?.animationName) || undefined,
    autoplay: prefersReducedMotion ? undefined : "",
    "camera-orbit":
      textValue(modelViewer?.cameraOrbit) || "90deg 75deg 105%",
    "camera-controls": "",
    loading: "lazy",
    poster: resolvedPoster || undefined,
    ref: modelViewerRef,
    reveal: "auto",
    src: resolvedSrc,
    style: {
      display: "block",
      width: "100%",
      height: "100%",
      background: "radial-gradient(circle at 50% 35%, #263c45, #101820)",
    },
    "touch-action": "pan-y",
  });

  const statusMessage =
    loadStatus === "error"
      ? "The model preview could not be loaded."
      : "Loading 3D preview…";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: compact ? 200 : { xs: 240, sm: 320 },
        my: compact ? 1.25 : 2,
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid rgba(255, 255, 255, 0.13)",
        backgroundColor: "#101820",
      }}
    >
      {preview}
      {loadStatus !== "loaded" && !resolvedPoster && (
        <Typography
          role="status"
          sx={{
            ...viewerPanelBodySx,
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          {statusMessage}
        </Typography>
      )}
    </Box>
  );
};

const DownloadIcon = () => (
  <SvgIcon
    aria-hidden="true"
    viewBox="0 0 24 24"
    sx={viewerActionButtonIconSx}
  >
    <path
      d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </SvgIcon>
);

const MetadataRow = ({ children, compact, label }) => (
  <Box>
    <Typography
      component="span"
      sx={{ color: "rgba(242, 255, 252, 0.68)", fontSize: compact ? "0.66rem" : "0.76rem", fontWeight: 800, textTransform: "uppercase" }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        mt: 0.25,
        fontSize: compact ? "0.78rem" : undefined,
        lineHeight: compact ? 1.4 : 1.55,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const LicensedAssetCard = ({ asset, compact }) => {
  const downloads = asArray(asset?.downloads);
  const sourceTitle = textValue(asset?.sourceTitle) || "Original source";
  const sourceCreator = textValue(asset?.sourceCreator);

  return (
    <Paper
      variant="outlined"
      sx={{
        ...(compact
          ? getViewerPanelPaddingSx(false)
          : { p: { xs: 2, sm: 2.5 } }),
        color: "inherit",
        backgroundColor: "rgba(255, 255, 255, 0.035)",
        borderColor: "rgba(255, 255, 255, 0.12)",
      }}
    >
      <Typography
        component="h3"
        sx={{ fontSize: compact ? "0.9rem" : "1.08rem", fontWeight: 800 }}
      >
        {textValue(asset?.title) || "Licensed asset"}
      </Typography>
      {asset?.description && (
        <Typography sx={{ mt: compact ? 0.5 : 0.75, color: "rgba(242, 255, 252, 0.72)", fontSize: compact ? "0.78rem" : undefined, lineHeight: compact ? 1.45 : 1.6 }}>
          {textValue(asset.description)}
        </Typography>
      )}

      {asset?.modelViewer?.src && (
        <ModelViewerPreview asset={asset} compact={compact} />
      )}

      <Stack spacing={compact ? 1 : 1.5} sx={{ mt: compact ? 1.25 : 2 }}>
        {(asset?.sourceTitle || asset?.sourceCreator || asset?.sourceUrl) && (
          <MetadataRow compact={compact} label="Source">
            <ExternalLink href={asset?.sourceUrl}>
              {sourceTitle}
              {sourceCreator ? ` — ${sourceCreator}` : ""}
            </ExternalLink>
          </MetadataRow>
        )}
        {(asset?.sourceLicenseName || asset?.sourceLicenseUrl) && (
          <MetadataRow compact={compact} label="Source license">
            <ExternalLink href={asset?.sourceLicenseUrl}>
              {textValue(asset?.sourceLicenseName) || "View source license"}
            </ExternalLink>
          </MetadataRow>
        )}
        {(asset?.adaptationLicenseName || asset?.adaptationLicenseUrl) && (
          <MetadataRow compact={compact} label="Adaptation license">
            <ExternalLink href={asset?.adaptationLicenseUrl}>
              {textValue(asset?.adaptationLicenseName) || "View adaptation license"}
            </ExternalLink>
          </MetadataRow>
        )}
        {asset?.changesMade && (
          <MetadataRow compact={compact} label="Changes made">
            {textValue(asset.changesMade)}
          </MetadataRow>
        )}
      </Stack>

      {downloads.length > 0 && (
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: compact ? 1.5 : 2.5 }}>
          {downloads.map((download, index) => {
            const format = textValue(download?.format);
            const label = textValue(download?.label) || "Download asset";
            const fullLabel = format ? `${label} (${format})` : label;
            const actionLabel = format
              ? `Download ${format.toUpperCase()}`
              : "Download asset";

            return (
              <Box
                key={`${download?.url || label}-${index}`}
                sx={{ maxWidth: "100%" }}
              >
                <Button
                  component="a"
                  download
                  href={download?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={fullLabel}
                  title={fullLabel}
                  sx={{
                    ...viewerPillActionButtonSx,
                    maxWidth: "100%",
                  }}
                >
                  <DownloadIcon />
                  <Box
                    component="span"
                    className="control-label"
                    sx={viewerActionButtonLabelSx}
                  >
                    {actionLabel}
                  </Box>
                </Button>
                {download?.licenseName && (
                  <Typography sx={{ mt: 1, color: "rgba(242, 255, 252, 0.68)", fontSize: "0.72rem" }}>
                    License: {textValue(download.licenseName)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

const AttributionCard = ({ attribution, compact }) => {
  const primaryLabel =
    textValue(attribution?.label) || textValue(attribution?.sourceTitle) || "Attribution";

  return (
    <Box>
      <Typography
        component="h3"
        sx={{ fontSize: compact ? "0.88rem" : undefined, fontWeight: 800 }}
      >
        {primaryLabel}
      </Typography>
      {(attribution?.sourceTitle || attribution?.creator || attribution?.sourceUrl) && (
        <Typography sx={{ mt: 0.35, fontSize: compact ? "0.78rem" : undefined, lineHeight: compact ? 1.4 : 1.55 }}>
          <ExternalLink href={attribution?.sourceUrl}>
            {textValue(attribution?.sourceTitle) || "Original source"}
            {attribution?.creator ? ` — ${textValue(attribution.creator)}` : ""}
          </ExternalLink>
        </Typography>
      )}
      {(attribution?.licenseName || attribution?.licenseUrl) && (
        <Typography sx={{ mt: 0.35, color: "rgba(242, 255, 252, 0.72)", fontSize: compact ? "0.78rem" : undefined }}>
          License:{" "}
          <ExternalLink href={attribution?.licenseUrl}>
            {textValue(attribution?.licenseName) || "View license"}
          </ExternalLink>
        </Typography>
      )}
      {attribution?.notes && (
        <Typography sx={{ mt: compact ? 0.5 : 0.75, color: "rgba(242, 255, 252, 0.72)", fontSize: compact ? "0.78rem" : undefined, lineHeight: compact ? 1.45 : 1.6 }}>
          {textValue(attribution.notes)}
        </Typography>
      )}
    </Box>
  );
};

const FundingCard = ({ compact, credit }) => {
  const heading = textValue(credit?.funder) || "Funding support";
  const details = [textValue(credit?.program), textValue(credit?.grantNumber)]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box>
      <Typography
        component="h3"
        sx={{ fontSize: compact ? "0.88rem" : undefined, fontWeight: 800 }}
      >
        <ExternalLink href={credit?.url}>{heading}</ExternalLink>
      </Typography>
      {details && (
        <Typography sx={{ mt: 0.35, fontSize: compact ? "0.78rem" : undefined }}>
          {details}
        </Typography>
      )}
      {credit?.statement && (
        <Typography sx={{ mt: compact ? 0.5 : 0.75, color: "rgba(242, 255, 252, 0.72)", fontSize: compact ? "0.78rem" : undefined, lineHeight: compact ? 1.45 : 1.6 }}>
          {textValue(credit.statement)}
        </Typography>
      )}
    </Box>
  );
};

const CreditsContent = ({ compact = false, credits }) => {
  const bodyBlocks = asArray(credits?.bodyBlocks);
  const licensedAssets = asArray(credits?.licensedAssets);
  const attributions = asArray(credits?.attributions);
  const funding = asArray(credits?.funding);
  const isEmpty = !hasCreditsContent(credits);

  if (isEmpty) {
    return (
      <Typography sx={{ py: 2, color: "rgba(242, 255, 252, 0.72)", lineHeight: 1.6 }}>
        {EMPTY_CREDITS_MESSAGE}
      </Typography>
    );
  }

  return (
    <Box>
      {bodyBlocks.length > 0 && (
        <Stack spacing={compact ? 1.25 : 2} component="section" aria-label="Credits information">
          {bodyBlocks.map((block, index) => (
            <BodyBlock
              key={block?.id || `body-block-${index}`}
              block={block}
              compact={compact}
            />
          ))}
        </Stack>
      )}

      {licensedAssets.length > 0 && (
        <CreditSection compact={compact} title="Licensed & adapted assets">
          <Stack spacing={compact ? 1.25 : 2}>
            {licensedAssets.map((asset, index) => (
              <LicensedAssetCard
                key={asset?.id || `${asset?.title || "licensed-asset"}-${index}`}
                asset={asset}
                compact={compact}
              />
            ))}
          </Stack>
        </CreditSection>
      )}

      {attributions.length > 0 && (
        <CreditSection compact={compact} title="Attributions">
          <Stack spacing={compact ? 1.25 : 2} divider={<Divider flexItem />}>
            {attributions.map((attribution, index) => (
              <AttributionCard
                key={attribution?.id || `${attribution?.label || "attribution"}-${index}`}
                attribution={attribution}
                compact={compact}
              />
            ))}
          </Stack>
        </CreditSection>
      )}

      {funding.length > 0 && (
        <CreditSection compact={compact} title="Funding">
          <Stack spacing={compact ? 1.25 : 2} divider={<Divider flexItem />}>
            {funding.map((credit, index) => (
              <FundingCard
                key={credit?.id || `${credit?.funder || "funding"}-${index}`}
                compact={compact}
                credit={credit}
              />
            ))}
          </Stack>
        </CreditSection>
      )}
    </Box>
  );
};

export { EMPTY_CREDITS_MESSAGE, hasCreditsContent };
export default CreditsContent;
